import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AppConfig } from '../types/config';
import { loadConfig } from '../services/appConfig';

/**
 * Configuration Context
 *
 * Provides application configuration to all components via React Context
 *
 * Requirements:
 * - Load configuration on app initialization
 * - Provide configuration to all components
 * - Handle loading state (show spinner)
 * - Handle error state (show error page with retry)
 * - Prevent app from rendering until config loaded
 */

interface ConfigContextValue {
  config: AppConfig | null;
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

/**
 * Configuration Provider Component
 *
 * Loads configuration on mount and provides it to all child components
 *
 * States:
 * - loading: true while configuration is being fetched
 * - error: true after configuration loading fails
 * - config: contains loaded configuration when successful
 *
 * The provider prevents children from rendering until configuration is loaded
 * or an error occurs.
 */
export function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState(false);

  /**
   * Loads configuration from settings.json
   *
   * Sets loading state, attempts to load config, and updates state based on result
   */
  const load = async () => {
    setLoading(true);
    setError(false);

    try {
      const loadedConfig = await loadConfig();
      setConfig(loadedConfig);
      setError(false);
    } catch (err) {
      setError(true);
      setConfig(null);

      // Log error for debugging
      console.error('Configuration loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load configuration on component mount
   */
  useEffect(() => {
    load();
  }, []);

  const value: ConfigContextValue = {
    config,
    loading,
    error,
    reload: load,
  };

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

/**
 * Hook to access configuration context
 *
 * @returns ConfigContextValue containing config, loading, error, and reload function
 * @throws Error if used outside of ConfigProvider
 *
 * Usage:
 * ```tsx
 * const { config, loading, error, reload } = useConfig();
 *
 * if (loading) return <Spinner />;
 * if (error) return <ErrorPage onRetry={reload} />;
 *
 * // Use config.apiBaseUrl, config.s3BucketName, etc.
 * ```
 */
export function useConfig(): ConfigContextValue {
  const context = useContext(ConfigContext);

  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }

  return context;
}
